import { Injectable, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  WarningRule,
  WarningResult,
  WarningCondition
} from '../../models/validation-warning.model';
import { ValidationContextService } from './validation-context.service';
import { readWarningGateValue } from './validators/warning-eval.helper';

/**
 * FieldWarningService
 *
 * Evaluates non-blocking validation warnings for form fields.
 * Warnings are suggestions to improve data quality, displayed in BLACK text with (*).
 *
 * Purpose:
 * - Provide data quality suggestions in Edited Mode
 * - Non-blocking: Users can ignore warnings and still submit
 * - Different from errors (RED): Errors block submission, warnings don't
 *
 * Usage:
 * 1. Define warning rules in role*-form.json schema
 * 2. Component calls evaluateWarnings() when form values change
 * 3. Display warning results in template next to field labels
 */
@Injectable({
  providedIn: 'root'
})
export class FieldWarningService {
  private ctx = inject(ValidationContextService);

  /**
   * Evaluate all warning rules for a specific field
   *
   * @param control - The FormControl to evaluate
   * @param warningRules - Warning rules from schema
   * @param allFormValues - All form values (for cross-field validation)
   * @param isEditedMode - Whether in edited mode (warnings only show in edited mode)
   * @returns Array of triggered warnings
   */
  evaluateWarnings(
    control: FormControl,
    warningRules: WarningRule[],
    allFormValues: any,
    isEditedMode: boolean
  ): WarningResult[] {
    // Don't evaluate warnings if not in edited mode
    if (!isEditedMode) {
      return [];
    }

    const results: WarningResult[] = [];
    const value = control.value;

    for (const rule of warningRules) {
      // Skip if rule should only show in edited mode and we're not in edited mode
      if (rule.showInEditedModeOnly && !isEditedMode) {
        continue;
      }

      // Check if warning condition is met
      const isTriggered = this.checkWarningCondition(
        value,
        rule.condition,
        allFormValues
      );

      if (isTriggered) {
        results.push({
          id: rule.id,
          message: rule.condition.message,
          severity: rule.severity
        });
      }
    }

    return results;
  }

  /**
   * Check if a warning condition is met
   *
   * @param value - The field value to check
   * @param condition - The warning condition definition
   * @param allValues - All form values (for cross-field checks)
   * @returns true if warning should trigger
   */
  private checkWarningCondition(
    value: any,
    condition: WarningCondition,
    allValues: any
  ): boolean {
    // Special handling for isEmpty and conditionalRequired - they check empty values
    const isEmptyValue = value === null || value === undefined || value === '';

    if (condition.type === 'isEmpty') {
      return isEmptyValue; // Trigger warning if field is empty
    }

    if (condition.type === 'conditionalRequired') {
      // Only trigger if field is empty AND dependency condition is met
      if (!isEmptyValue) {
        return false; // Field has value, no warning needed
      }
      // Check if dependency condition is met
      if (condition.dependsOn) {
        const dependentFieldValue = allValues[condition.dependsOn.field];
        return condition.dependsOn.values.includes(dependentFieldValue);
      }
      return false;
    }

    // For all other warning types, skip if value is empty
    if (isEmptyValue) {
      return false;
    }

    // Check conditional logic: Only evaluate warning if conditionalOn is met
    if (condition.conditionalOn) {
      // Read through the SAME reader the pure helper uses for the sheet's red
      // asterisk, so the inline warning and the asterisk cannot disagree: the
      // gate sheet's nested slice, else the flat same-sheet field. Filing-info
      // aliases are translated to the active phase's slot key so the lookup
      // hits live data in both Phase 1 and Phase 2.
      const expectedValue = condition.conditionalOn.equals;
      const conditionalValue: any = readWarningGateValue(
        allValues,
        (sheetKey) => this.ctx.resolveSheetKey(sheetKey) ?? sheetKey,
        condition.conditionalOn.sheet,
        condition.conditionalOn.field
      );

      // If conditional field value doesn't match expected value, skip this warning
      if (Array.isArray(expectedValue)) {
        if (!expectedValue.includes(conditionalValue)) {
          return false; // Condition not met, don't show warning
        }
      } else {
        if (conditionalValue !== expectedValue) {
          return false; // Condition not met, don't show warning
        }
      }
    }

    switch (condition.type) {
      case 'dateInFuture':
        return this.isDateInFuture(value);

      case 'dateOlderThanYears':
        // `??`, not `||`: an explicit `years: 0` is a real threshold. Matches
        // the pure helper behind the sheet's red asterisk.
        return this.isDateOlderThan(value, condition.years ?? 10);

      case 'dateComparison':
        return this.checkDateComparison(
          value,
          condition.comparisonOperator || 'lte',
          condition.compareWith,
          allValues
        );

      case 'valueOutOfRange':
        return this.isOutOfRange(
          value,
          condition.min,
          condition.max
        );

      case 'patternMismatch':
        return this.hasPatternMismatch(value, condition.pattern);

      case 'inconsistentData':
        return this.hasInconsistentData(
          value,
          condition.relatedField,
          allValues
        );

      case 'deprecatedValue':
        return this.isDeprecatedValue(value, condition.deprecatedValues);

      case 'custom':
        return this.evaluateCustomCondition(
          value,
          condition.expression || '',
          allValues
        );

      default:
        console.warn(`Unknown warning condition type: ${condition.type}`);
        return false;
    }
  }

  /**
   * Check if date is in the future
   */
  private isDateInFuture(date: Date | string): boolean {
    if (!date) return false;

    const dateValue = new Date(date);
    if (isNaN(dateValue.getTime())) return false; // Invalid date

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to midnight for fair comparison

    return dateValue > today;
  }

  /**
   * Check if date is older than specified years
   */
  private isDateOlderThan(date: Date | string, years: number): boolean {
    if (!date) return false;

    const dateValue = new Date(date);
    if (isNaN(dateValue.getTime())) return false; // Invalid date

    const cutoffDate = new Date();
    cutoffDate.setFullYear(cutoffDate.getFullYear() - years);

    return dateValue < cutoffDate;
  }

  /**
   * Check date comparison with another field (cross-sheet or cross-field)
   *
   * @param value - Current field date value
   * @param operator - Comparison operator (lte, lt, gte, gt, eq, ne)
   * @param compareWith - Reference to the field to compare with
   * @param allValues - All form values including cross-sheet values
   * @returns true if comparison condition is met (warning should trigger)
   */
  private checkDateComparison(
    value: Date | string,
    operator: 'lte' | 'lt' | 'gte' | 'gt' | 'eq' | 'ne',
    compareWith: { sheet: string; field: string } | undefined,
    allValues: any
  ): boolean {
    if (!value || !compareWith || !allValues) {
      console.warn('Missing required parameters for date comparison', {
        value,
        compareWith,
        allValues: allValues ? 'present' : 'missing'
      });
      return false;
    }

    // Get the comparison value from allValues (cross-sheet access).
    // Access pattern: allValues[sheetKey][fieldQname].
    //
    // Filing-info aliases: schemas may reference the filing-info sheet as
    // either "filingInfo" (role1 / Phase 2) or "filingPrerequisites" (role0 /
    // Phase 1). Under PR-D's strict phase-aware hydration only one slot is
    // populated. Translate either alias to whichever state key is active in
    // the current phase so cross-sheet warnings resolve against live data.
    const resolvedSheetKey = this.ctx.resolveSheetKey(compareWith.sheet);
    const sheetData = allValues[resolvedSheetKey as any];
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`📊 checkDateComparison - Looking for sheet: ${compareWith.sheet}, field: ${compareWith.field}`);
    // console.log(`📊 Sheet data:`, sheetData);

    if (!sheetData) {
      // Log both the schema-declared alias and the resolved active-phase key
      // so debuggers see whether the bag is missing the data outright or just
      // keyed under the other filing-info alias.
      console.warn(
        `Sheet not found in allValues — schema alias "${compareWith.sheet}", ` +
        `resolved key "${resolvedSheetKey}"`
      );
      return false;
    }

    const compareValue = sheetData[compareWith.field];
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`📊 Compare value:`, compareValue);

    if (!compareValue) {
      console.warn(`Field "${compareWith.field}" not found in sheet "${compareWith.sheet}"`);
      return false;
    }

    // Convert both dates to Date objects
    const currentDate = new Date(value);
    const targetDate = new Date(compareValue);

    // Validate dates
    if (isNaN(currentDate.getTime())) {
      return false;
    }
    if (isNaN(targetDate.getTime())) {
      return false;
    }

    // Normalize dates to midnight for fair comparison (ignore time)
    currentDate.setHours(0, 0, 0, 0);
    targetDate.setHours(0, 0, 0, 0);

    const currentTime = currentDate.getTime();
    const targetTime = targetDate.getTime();

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`📊 Date comparison: ${currentDate.toISOString().split('T')[0]} (${operator}) ${targetDate.toISOString().split('T')[0]}`);

    // Perform comparison based on operator
    switch (operator) {
      case 'lte': // Less than or equal (trigger warning if current <= target)
        const result = currentTime <= targetTime;
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`📊 Result (lte): ${result}`);
        return result;
      case 'lt':  // Less than (trigger warning if current < target)
        const ltResult = currentTime < targetTime;
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`📊 Result (lt): ${ltResult}`);
        return ltResult;
      case 'gte': // Greater than or equal (trigger warning if current >= target)
        const gteResult = currentTime >= targetTime;
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`📊 Result (gte): ${gteResult}`);
        return gteResult;
      case 'gt':  // Greater than (trigger warning if current > target)
        const gtResult = currentTime > targetTime;
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // console.log(`📊 Result (gt): ${gtResult}`);
        return gtResult;
      case 'eq':  // Equal (trigger warning if current == target)
        return currentTime === targetTime;
      case 'ne':  // Not equal (trigger warning if current != target)
        return currentTime !== targetTime;
      default:
        return false;
    }
  }

  /**
   * Check if numeric value is outside allowed range
   */
  private isOutOfRange(
    value: any,
    min?: number,
    max?: number
  ): boolean {
    const numValue = Number(value);
    if (isNaN(numValue)) return false; // Not a number

    if (min !== undefined && numValue < min) return true;
    if (max !== undefined && numValue > max) return true;

    return false;
  }

  /**
   * Check if value matches a pattern (soft validation)
   */
  private hasPatternMismatch(value: string, pattern?: string): boolean {
    if (!pattern || !value) return false;

    try {
      const regex = new RegExp(pattern);
      return !regex.test(String(value));
    } catch (error) {
      return false;
    }
  }

  /**
   * Check for inconsistent data across fields
   */
  private hasInconsistentData(
    value: any,
    relatedField?: string,
    allValues?: any
  ): boolean {
    if (!relatedField || !allValues) return false;

    const relatedValue = allValues[relatedField];
    if (!relatedValue) return false;

    // Example: Check if start date is after end date
    // This is a generic check - specific logic depends on field types
    const currentDate = new Date(value);
    const relatedDate = new Date(relatedValue);

    if (!isNaN(currentDate.getTime()) && !isNaN(relatedDate.getTime())) {
      return currentDate > relatedDate;
    }

    return false;
  }

  /**
   * Check if value is deprecated
   */
  private isDeprecatedValue(
    value: any,
    deprecatedValues?: string[]
  ): boolean {
    if (!deprecatedValues || deprecatedValues.length === 0) return false;

    return deprecatedValues.includes(String(value));
  }

  /**
   * Evaluate custom JavaScript expression
   *
   * @param value - Current field value
   * @param expression - JavaScript expression to evaluate
   * @param allValues - All form values for cross-field checks
   * @returns true if expression evaluates to true
   */
  private evaluateCustomCondition(
    value: any,
    expression: string,
    allValues: any
  ): boolean {
    if (!expression) return false;

    try {
      // Create a safe evaluation context
      // Available variables: value, allValues
      const evalFunction = new Function('value', 'allValues', `return ${expression};`);
      const result = evalFunction(value, allValues);
      return Boolean(result);
    } catch (error) {
      console.error('Error evaluating custom warning expression:', expression, error);
      return false;
    }
  }

  /**
   * Batch evaluate warnings for multiple fields
   *
   * @param controls - Map of field qname to FormControl
   * @param warningRulesByField - Map of field qname to warning rules
   * @param allFormValues - All form values
   * @param isEditedMode - Whether in edited mode
   * @returns Map of field qname to warning results
   */
  batchEvaluateWarnings(
    controls: { [qname: string]: FormControl },
    warningRulesByField: { [qname: string]: WarningRule[] },
    allFormValues: any,
    isEditedMode: boolean
  ): { [qname: string]: WarningResult[] } {
    const warningsByField: { [qname: string]: WarningResult[] } = {};

    Object.keys(warningRulesByField).forEach(qname => {
      const control = controls[qname];
      const rules = warningRulesByField[qname];

      if (control && rules && rules.length > 0) {
        const warnings = this.evaluateWarnings(
          control,
          rules,
          allFormValues,
          isEditedMode
        );

        if (warnings.length > 0) {
          warningsByField[qname] = warnings;
        }
      }
    });

    return warningsByField;
  }
}
